/**
 * Landing — marketing home. Renders inside PublicShell (nav + footer
 * come from the shell).
 *
 * Layout mirrors src/frontend/components/components-desktop/public-screens.jsx
 * (LandingScreen):
 *   1. Hero — 88px display headline, eyebrow, body, primary + ghost
 *      CTAs, social-proof avatars, right-side floating card stack
 *      with a "real note" prop dropped on top.
 *   2. How it works — three-column StepCard grid on a bg-2 strip.
 *   3. Real notes — 4-up NoteTile row pulled from sample data.
 *   4. Premium — dark ink section with two-column copy + feature pill
 *      list, coral CTA.
 *   5. Final CTA — 72px italic display, coral pill.
 *
 * All copy lives inline; no fetches. The sample data (FLOATING_CARDS,
 * SAMPLE_NOTES) is intentionally hand-rolled — a real "recent notes
 * across the platform" endpoint would need privacy + moderation
 * curation that isn't in scope here.
 */
import { Link } from 'react-router-dom';
import { Btn, Eyebrow, FloatingCard, Icons, NoteTile, Photo, StepCard, type NoteData } from '@/components';

const FLOATING_CARDS = [
  { rotate: -7, top: 20,  left: 0,   z: 1, variant: 2 as const, initial: 'A', name: 'Alex',  score: 8.1 },
  { rotate:  4, top: 70,  left: 120, z: 2, variant: 1 as const, initial: 'M', name: 'Maya',  score: 7.8, primary: true },
  { rotate: -3, top: 300, left: 50,  z: 1, variant: 3 as const, initial: 'J', name: 'Jonah', score: 8.7 },
];

const SAMPLE_NOTES: NoteData[] = [
  { id: '1', yes: true,  when: '2h ago', comment: 'The ceramics line got me. Swipe right (metaphorically).' },
  { id: '2', yes: true,  when: 'yesterday', comment: "Photo 3 is doing all the work but also? It's working." },
  { id: '3', yes: false, when: '3d ago', comment: 'Bio reads like a LinkedIn About section. Loosen up.' },
  { id: '4', yes: true,  when: 'last week', comment: 'You like Frank Ocean AND have a cat. Marry me.' },
];

const PREMIUM_FEATURES = [
  { icon: Icons.chat,  title: 'Send DMs',           body: 'The only way to start a real conversation.' },
  { icon: Icons.spark, title: 'All 3 AI reviewers', body: 'Unlimited reads from Friend, Critic, Hype Man.' },
];

export function LandingPage() {
  return (
    <div>
      {/* ── HERO ────────────────────────────────────────────── */}
      <section
        className="landing-hero"
        style={{
          padding: '70px 40px 80px',
          display: 'grid',
          gridTemplateColumns: '1.05fr 0.95fr',
          gap: 56,
          alignItems: 'center',
          maxWidth: 1480,
          margin: '0 auto',
        }}
      >
        <div>
          <Eyebrow tone="coral" letter={3} style={{ marginBottom: 18 }}>Anonymous · 18+</Eyebrow>
          <h1
            className="serif landing-hero-headline"
            style={{
              fontSize: 88,
              fontWeight: 500,
              letterSpacing: -2.5,
              lineHeight: 0.96,
              margin: 0,
              textWrap: 'balance' as const,
            }}
          >
            Honest feedback on your <span style={{ fontStyle: 'italic' }}>dating profile.</span>
          </h1>
          <p
            className="landing-hero-body"
            style={{
              fontSize: 19,
              color: 'var(--ink-2)',
              lineHeight: 1.5,
              marginTop: 24,
              maxWidth: 520,
            }}
          >
            Build a card. Share the link. Get anonymous ratings and notes — from the people you trust, the ones you don't, and everyone in between.
          </p>

          <div style={{ display: 'flex', gap: 12, marginTop: 32, alignItems: 'center' }}>
            <Link to="/auth/signup" style={{ textDecoration: 'none' }}>
              <Btn variant="primary" size="lg" icon={<Icons.arrow size={16} />}>
                Build my card
              </Btn>
            </Link>
          </div>

          <div
            style={{
              marginTop: 36,
              display: 'flex',
              alignItems: 'center',
              gap: 18,
              fontSize: 12,
              color: 'var(--ink-3)',
            }}
          >
            <div style={{ display: 'flex' }}>
              {[1, 2, 3, 4, 5].map((v) => (
                <div
                  key={v}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 32,
                    overflow: 'hidden',
                    marginLeft: v > 1 ? -10 : 0,
                    border: '2px solid var(--bg)',
                    flexShrink: 0,
                  }}
                >
                  <Photo variant={v as 1 | 2 | 3 | 4 | 5} rounded={32} />
                </div>
              ))}
            </div>
            <div>Built for raters who actually read the bio.</div>
          </div>
        </div>

        {/* Right: stack of floating cards + one tilted note */}
        <div className="landing-hero-cards" style={{ position: 'relative', height: 580 }}>
          {FLOATING_CARDS.map((c, i) => <FloatingCard key={i} {...c} />)}
          <div
            style={{
              position: 'absolute',
              top: 360,
              right: 0,
              width: 280,
              background: 'var(--surface)',
              borderRadius: 18,
              padding: 16,
              boxShadow: 'var(--shadow-lift)',
              transform: 'rotate(3deg)',
              zIndex: 5,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 26,
                  background: 'var(--coral)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                ♥
              </div>
              <Eyebrow size="sm" letter={1}>Would date · 2h ago</Eyebrow>
            </div>
            <div className="serif" style={{ fontSize: 15, lineHeight: 1.45 }}>
              “The ceramics line got me. Swipe right (metaphorically).”
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────── */}
      <section
        id="how-it-works"
        className="landing-section"
        style={{
          padding: '80px 40px',
          background: 'var(--bg-2)',
          borderTop: '1px solid var(--line)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div style={{ maxWidth: 1480, margin: '0 auto' }}>
          <Eyebrow tone="coral" letter={3} style={{ marginBottom: 14 }}>How it works</Eyebrow>
          <h2
            className="serif landing-h2"
            style={{
              fontSize: 56,
              fontWeight: 500,
              letterSpacing: -1.5,
              lineHeight: 1.05,
              margin: 0,
              textWrap: 'balance' as const,
            }}
          >
            Three steps. <span style={{ fontStyle: 'italic' }}>Maybe four,</span> if you count panic-checking it every five minutes.
          </h2>

          <div className="landing-3up" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28, marginTop: 56 }}>
            <StepCard
              num="01"
              title="Build your card"
              body="Photos, bio, music. The kit shows you how it'll look as it comes together — no guessing."
            />
            <StepCard
              num="02"
              title="Share the link"
              body="A short, branded URL (wydm.app/p/your-name). Drop it in your IG bio, post the share card to Stories, send it in a group chat."
            />
            <StepCard
              num="03"
              title="Read what comes back"
              body="Aggregate score. Individual notes. A “would date %” that updates as people rate. No swiping, just feedback."
            />
          </div>
        </div>
      </section>

      {/* ── REAL NOTES ──────────────────────────────────────── */}
      <section className="landing-section" style={{ padding: '90px 40px', maxWidth: 1480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 36, gap: 24, flexWrap: 'wrap' }}>
          <h2
            className="serif landing-h2"
            style={{
              fontSize: 48,
              fontWeight: 500,
              letterSpacing: -1,
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            <span style={{ fontStyle: 'italic' }}>Real</span> notes. Read them all.
          </h2>
        </div>
        <div className="landing-4up" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {SAMPLE_NOTES.map((r) => <NoteTile key={r.id} r={r} />)}
        </div>
      </section>

      {/* ── PREMIUM ─────────────────────────────────────────── */}
      <section className="landing-section" style={{ padding: '80px 40px', background: 'var(--ink)', color: 'var(--bg)' }}>
        <div
          className="landing-premium"
          style={{
            maxWidth: 1480,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 56,
            alignItems: 'center',
          }}
        >
          <div>
            <Eyebrow tone="coral" letter={3} style={{ marginBottom: 18 }}>WYDM Premium</Eyebrow>
            <h2
              className="serif landing-premium-h2"
              style={{
                fontSize: 64,
                fontWeight: 500,
                letterSpacing: -1.5,
                lineHeight: 1.05,
                margin: 0,
                textWrap: 'balance' as const,
              }}
            >
              For when notes <span style={{ fontStyle: 'italic' }}>aren't enough.</span>
            </h2>
            <p style={{ fontSize: 17, opacity: 0.7, lineHeight: 1.5, marginTop: 20, maxWidth: 460 }}>
              Three features. Pay once. Cancel anytime. €4.99/mo on the annual plan.
            </p>
            <div style={{ marginTop: 28, display: 'flex', gap: 12 }}>
              <Link to="/auth/signup" style={{ textDecoration: 'none' }}>
                <Btn variant="primary" size="lg">Get Premium →</Btn>
              </Link>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {PREMIUM_FEATURES.map((it) => (
              <div
                key={it.title}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: 20,
                  borderRadius: 18,
                  background: 'rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'var(--coral)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <it.icon size={18} stroke={2.2} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{it.title}</div>
                  <div style={{ fontSize: 13, opacity: 0.65, marginTop: 4, lineHeight: 1.4 }}>{it.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section className="landing-section" style={{ padding: '80px 40px', maxWidth: 1480, margin: '0 auto', textAlign: 'center' }}>
        <h2
          className="serif landing-cta-h2"
          style={{
            fontSize: 72,
            fontWeight: 500,
            letterSpacing: -2,
            lineHeight: 1,
            margin: 0,
            textWrap: 'balance' as const,
          }}
        >
          Would you <span style={{ fontStyle: 'italic' }}>date you?</span>
        </h2>
        <p style={{ fontSize: 17, color: 'var(--ink-3)', marginTop: 18, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>
          90 seconds to build. 30 seconds to share. The rest is on you.
        </p>
        <div style={{ marginTop: 28, display: 'inline-flex' }}>
          <Link to="/auth/signup" style={{ textDecoration: 'none' }}>
            <Btn variant="primary" size="lg" icon={<Icons.arrow size={18} />}>Find out</Btn>
          </Link>
        </div>
      </section>
    </div>
  );
}
